// Phase 24 — Strategy-Switch (EUA-01 / D-02 / D-03). Mollie-Kurs → MollieMethodsBlock; sonst klassische paymentMethodSection.
import { Alert, Button, Collapse, Spin } from "antd"
import FormItem from "antd/lib/form/FormItem"
import TextArea from "antd/lib/input/TextArea"
import React, { FC, memo, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
    GetCourseDetailsByIdPublicQuery,
    GetUpdatedBookerQuery,
    NewBookingInput,
    PaymentInterval,
    SettingEndUserAppQuery,
    useGetAvailablePaymentMethodsFromEndUserAppQuery,
    useGetCompanySettingsPaymentmethodsQuery,
} from "../../../../generated/graphql"
import { getPaymentMethodType, PaymentMethodType } from "../../../../helpers/getPaymentMethodType"
import { isPaymentMethodUsableForBooking } from "../../../../helpers/isPaymentMethodUsableForBooking"
import { margin } from "../../../../styles/layout"
import CourseLessonTable from "../../../../views/CourseLessonTable/CourseLessonTable"
import PaymentMethodTable, { CompanyMethods } from "../../../../views/PaymentMethodTable/PaymentMethodTable"
import type { MollieMethodRow } from "../../../../views/PaymentMethodTable/PaymentMethodTable"
import SummarySection from "../../../../views/SummarySection/SummarySection"
import { MollieMethodsBlock } from "../MollieMethodsBlock"
import { gateMollieMethodsForDevice } from "../../helpers/gateMollieMethodsForDevice"
import { ModeType, NewBookingInputKeys } from "../../BookingSummary/BookingSummary"
import css from "../StepSummary2.module.css"
import mollieCss from "./SummaryRight.mollie.module.css"
import { v4 } from "uuid"
import { useHistory } from "react-router-dom"
import { URL_RESULTS } from "../../../../models/url"
import { DownCircleTwoTone, ReloadOutlined, RightCircleFilled } from "@ant-design/icons"
import { blue } from "@ant-design/colors"
import { useMediaSize } from "../../../../layouts/Main/GetWindowDimensions/GetWindowDimensions"
import OptionalInputFields from "./OptionalInputFields"
import BillingAddressInput from "./OptionalInputFields/BillingAddressInput"
import LayoutForm from "../../../../views/LayoutForm/LayoutForm"
import ParticipationPrerequisites from "./ParticipationPrerequisites/ParticipationPrerequisites"
import BookingPricePreview from "../../BookingSummary/BookingPricePreview/BookingPricePreview"
import { courseOffersPaymentTypeChoice } from "../../BookingSummary/BookingPricePreview/paymentTypeChoice"
import { QuoteInfo, QUOTE_ERROR } from "../../BookingSummary/lateEntryQuote"
import ResultScreen from "../../../../views/ResultScreens/ResultScreen"

export type CompanyMethodsWithIds = { method: Extract<keyof CompanyMethods, string>; active: boolean; id: string }

type SummaryRightProps = {
    booker: GetUpdatedBookerQuery["booker"]
    // course: Course
    course: GetCourseDetailsByIdPublicQuery["courseByIdPublic"]
    mode: ModeType
    /** Voraussetzung fehlt, die Beschränkung erlaubt nur die Warteliste. */
    prerequisiteWaitlistOnly?: boolean
    updateBookingData: (key: NewBookingInputKeys, value: any) => void
    bookingData?: NewBookingInput
    liftSepaDebitData: (isValid: boolean) => void
    setCheckedOptions: (checkedOptions: { [id: string]: Array<string> }) => void
    checkedOptions: { [id: string]: Array<string> }
    setRequermentGroupsMet: (requermentGroupsMet: { [id: string]: boolean }) => void
    requermentGroupsMet: { [id: string]: boolean }
    showAlert?: boolean
    appSettings?: SettingEndUserAppQuery["settingEndUserAppPublic"]
    inputFieldValues: Array<{ [Key: string]: string }>
    setInputFieldValues: (values: Array<{ [Key: string]: string }>) => void
    selectedMollieMethod: string | null
    onMollieMethodSelect: (methodId: string | null) => void
    fullyCoveredByCredit: boolean
    onOpenAmountChange: (openAmount: number) => void
    onQuoteChange?: (quote: QuoteInfo | typeof QUOTE_ERROR | null) => void
    refreshNonce?: number
    onCourseOutdated?: () => void
    /** Server hat die gewählte Lastschrift mit PAYMENT_METHOD_INCOMPLETE abgelehnt (B-82808) */
    paymentMethodIncomplete?: boolean
}

const SummaryRight: FC<SummaryRightProps> = ({
    booker,
    course,
    mode,
    prerequisiteWaitlistOnly,
    updateBookingData,
    bookingData,
    liftSepaDebitData,
    setCheckedOptions,
    checkedOptions,
    setRequermentGroupsMet,
    requermentGroupsMet,
    showAlert,
    appSettings,
    inputFieldValues,
    setInputFieldValues,
    selectedMollieMethod,
    onMollieMethodSelect,
    fullyCoveredByCredit,
    onOpenAmountChange,
    onQuoteChange,
    refreshNonce,
    onCourseOutdated,
    paymentMethodIncomplete = false,
}) => {
    const mediaSize = useMediaSize()
    const { media } = mediaSize
    const history = useHistory()
    const { t } = useTranslation()

    // Mollie-Methoden laden (gibt leeres Array zurück wenn Mollie nicht aktiviert).
    // Mit `courseId` filtert das Backend kursabhängig: Kurse ohne Online-Zahlung
    // (course.allowMolliePayment=false bzw. Tenant-featureMollie aus) liefern eine
    // leere Liste — der Mollie-Block wird dann gar nicht erst angeboten. Damit
    // bleiben Tenant-Feature-Flag und Mollie-Connection-Status server-seitig die
    // Single-Source-of-Truth (statt einer rein clientseitigen allowMolliePayment-Deutung).
    const { data: mollieData } = useGetAvailablePaymentMethodsFromEndUserAppQuery({
        variables: { courseId: course?.id },
        fetchPolicy: "cache-first",
    })

    /** Aufbereitete Mollie-Methoden für die einheitliche Zahlungsmethoden-Tabelle */
    const mollieMethods: Array<MollieMethodRow> = useMemo(() => {
        const raw = mollieData?.getAvailablePaymentMethodsFromEndUserApp
        if (!raw || raw.length === 0) return []
        // Geraete-Gating (gemeinsamer Helfer, konsistent zu BookingSummary-Count +
        // MollieMethodsBlock): Apple Pay nur auf Apple-Geraeten/Safari (sonst Sackgasse
        // auf Mollies Hosted-Page). Vor dem Mapping filtern, damit die Vorauswahl
        // (mollieMethods[0]?.id) konsistent mit der angezeigten Liste bleibt.
        return gateMollieMethodsForDevice(raw).map((m) => ({
            id: m.id,
            description: m.description,
            imageUrl: m.imageUrl,
        }))
    }, [mollieData])

    if (!booker) {
        // Hinweis: Kein early-return — Hook-Calls weiter unten (useState/useEffect)
        // muessen konsistent zwischen Renders ablaufen. Optional Chaining (booker?.…)
        // im restlichen Body faengt die undefined-Zugriffe ab, bis der Redirect greift.
        history.push(URL_RESULTS.replace(":resultType", "loggedOut"))
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [methodType, setMethodType] = useState<PaymentMethodType>()
    const paymentMethod = booker?.paymentMethods?.find((pm) => pm.id === bookingData?.paymentMethodId)

    useEffect(() => {
        if (paymentMethod) {
            // B-82808: dieselbe Regel wie in PaymentMethodTable — nur die GEWÄHLTE Methode zählt.
            liftSepaDebitData(isPaymentMethodUsableForBooking(paymentMethod))
            setMethodType(getPaymentMethodType(paymentMethod))
        }
    }, [liftSepaDebitData, paymentMethod])

    // Phase 4 / Entscheidung D — Vorauswahl im Kombi-Modus (Online ADDITIV zu eigenen
    // Methoden). Regel: Der gueltige Teilnehmer-Default (`bookingData.paymentMethodId`,
    // im frueheren Wizard-Schritt aus `attendee.defaultPaymentMethod` gesetzt) hat
    // Vorrang und wird NICHT ueberschrieben. Existiert KEIN gueltiger eigener Default
    // und ist noch keine Mollie-Methode gewaehlt, wird die erste verfuegbare Mollie-
    // Methode aktiv vorausgewaehlt (Default sonst Online). Minimal-invasiv: greift nur
    // im Kombi-Modus; Pflicht-Modus forciert Online ohnehin ueber den MollieMethodsBlock,
    // klassische Kurse haben keine Mollie-Methoden.
    const isCombinedModeForPreselect = course?.allowMolliePayment === true && course?.mollieOptional === true && mollieMethods.length > 0
    const hasValidOwnDefault = Boolean(paymentMethod)
    const firstMollieMethodId = mollieMethods[0]?.id ?? null
    useEffect(() => {
        if (!isCombinedModeForPreselect) return
        if (hasValidOwnDefault) return
        if (selectedMollieMethod) return
        if (firstMollieMethodId) {
            onMollieMethodSelect(firstMollieMethodId)
        }
        // Bewusst nur bei Aenderung der Vorbedingungen — danach steuert der Nutzer frei.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCombinedModeForPreselect, hasValidOwnDefault, firstMollieMethodId])

    const {
        data: companyPaymentMethods,
        error: companySettingsError,
        loading: companySettingsLoading,
        refetch: companySettingRefetch,
    } = useGetCompanySettingsPaymentmethodsQuery({ fetchPolicy: "no-cache" }) // no cache because we want to refetch the data if there is an error
    const companyMethods: CompanyMethods | undefined = companyPaymentMethods?.settings

    const [companyMethodsWithIds, setCompanyMethodsWithIds] = useState<Array<CompanyMethodsWithIds>>([])

    useEffect(() => {
        // console.log("companyMethods", companyMethods)
        if (companyMethods) {
            const updatedCompanyMethodsWithIds: Array<CompanyMethodsWithIds> = []
            Object.entries(companyMethods).forEach(([key, value]) => {
                const typedKey = key as keyof Partial<CompanyMethods>
                const methodWithId: CompanyMethodsWithIds = {
                    method: typedKey,
                    active: Boolean(value),
                    id: v4(),
                }

                updatedCompanyMethodsWithIds.push(methodWithId)
            })

            setCompanyMethodsWithIds(updatedCompanyMethodsWithIds)
        }
    }, [companyMethods /* companySettingsError */])

    // REFETCH COMPANY SETTINGS IF THERE IS AN ERROR -----------------------------------------------------------------------------------------------------------

    const secondsToRetry = 10
    const maxRetryCount = 5
    const [retryCount, setRetryCount] = useState(0)
    const [countdown, setCountdown] = useState(0)

    useEffect(() => {
        if (retryCount < maxRetryCount && companySettingsError) {
            const timeout = setTimeout(() => {
                companySettingRefetch()
                setRetryCount((prevCount) => prevCount + 1)
            }, secondsToRetry * 1000)

            let remainingTime = secondsToRetry
            setCountdown(remainingTime)

            // Update the countdown every second
            const countdownInterval = setInterval(() => {
                remainingTime--
                setCountdown(remainingTime)
            }, 1000)

            // Clear the timeout and countdown interval on component unmount or retry limit reached
            return () => {
                clearTimeout(timeout)
                clearInterval(countdownInterval)
            }
        }
    }, [companySettingRefetch, companySettingsError, retryCount])

    // ---------------------------------------------------------------------------------------------------------------------------------------------------------

    // Issue #120: Als Render-Funktion aufrufen (wie notesSection), NICHT als inline
    // definierte Komponente `<PaymentMethodSection />`. Eine im Rumpf definierte
    // Komponente bekommt bei jedem Render eine neue Identität, React remountet dann
    // die PaymentMethodTable — und deren Rollback-Guards (#116/#118) verlieren ihren
    // Zustand, sodass der verspätete Fehler einer toten Instanz eine neuere Auswahl
    // zurückrollen konnte.
    const paymentMethodSection = () => {
        if (!companyMethodsWithIds || companyMethodsWithIds.length === 0) {
            if (retryCount === maxRetryCount) {
                // if (companySettingsError) { // DEBUG
                //     logger({ // DEBUG
                //         logLevel: LogLevelType.Error, // DEBUG
                //         msg: companySettingsError.message, // DEBUG
                //         additionalInfo: { stack: companySettingsError.stack }, // DEBUG
                //     }) // DEBUG
                // } // DEBUG
                return (
                    <>
                        <ResultScreen resultType='errorFetchingData' errorMsg={companySettingsError?.message} />
                    </>
                )
            }

            return (
                <Alert
                    style={{ textAlign: "center" }}
                    message={
                        <>
                            {companySettingsLoading || (countdown === 1 && <Spin />)}
                            <h3>Fehler beim Laden der Unternehmensdaten</h3>
                            <div>{`Wir versuchen es erneut in ${countdown} Sekunden`}</div>
                            <Button type='link' icon={<ReloadOutlined />} onClick={() => window.location.reload()}>
                                Seite jetzt neu laden
                            </Button>
                        </>
                    }
                    type='error'
                />
            )
        }
        if (!booker.paymentMethods) {
            return <>"PaymentMethods not found"</>
        }

        const handlePamentMethodChange = (e: { id: string }) => {
            updateBookingData("paymentMethodId", e.id)
            // Mollie-Auswahl aufheben wenn Standard-Methode gewählt wird
            onMollieMethodSelect(null)
        }

        // return <h3>{`To be done ==> ${attendee.firstname} ${attendee.lastname}`}</h3>
        return (
            <PaymentMethodTable
                paymentMethods={booker.paymentMethods}
                paymentMethodId={bookingData?.paymentMethodId ?? ""}
                setSelectedPayment={(e: { id: string }) => handlePamentMethodChange(e)}
                showHeader={false}
                booker={booker}
                companyMethodsWithIds={companyMethodsWithIds}
                liftSepaDebitData={liftSepaDebitData}
                mollieMethodSelected={!!selectedMollieMethod}
                mollieMethods={mollieMethods}
                selectedMollieMethod={selectedMollieMethod}
                onMollieMethodSelect={onMollieMethodSelect}
                showAlert={showAlert}
            />
        )
    }

    const notesSection = () => {
        // const [noticeBooker, setNoticeBooker] = useState<string>(bookingData?.noticeBooker ?? "")

        return (
            <LayoutForm columns={1}>
                <FormItem label={"Schreibe uns eine Nachricht"}>
                    {/* <TextArea rows={4} maxLength={256} allowClear onChange={(e) => setNoticeBooker(e.target.value)} /> */}
                    <TextArea
                        rows={4}
                        maxLength={256}
                        allowClear
                        value={bookingData?.noticeBooker ?? ""}
                        onChange={(e) => updateBookingData("noticeBooker", e.target.value)}
                        onBlur={(e) => updateBookingData("noticeBooker", e.target.value)}
                    />
                </FormItem>
            </LayoutForm>
        )
    }

    if (course === undefined) {
        return <div>No course found</div>
    }

    // Überschrift „Preisübersicht“ genau dann, wenn darunter der Zahlweisen-Schalter steht.
    const courseHasTwoAllowedPaymentOptions = courseOffersPaymentTypeChoice(course)
    const sectionGap = margin.s

    // Phase 24 — D-02 / D-03 — Strategy-Switch (Boolean-strict): nur wenn der Kurs
    // explizit `allowMolliePayment === true` UND mindestens eine Mollie-Methode
    // server-seitig verfuegbar ist (=> Subscription.featureMollie===true + Mollie-
    // Connection aktiv + enabledMollieMethods nicht leer; siehe Backend-Resolver
    // `getAvailablePaymentMethodsFromEndUserApp`), wird der Mollie-Block angezeigt.
    // `null` / `undefined` / `false` fallen auf den klassischen Pfad (Radio-Liste)
    // — wichtig fuer EUA-08 (Byte-Identity klassische Buchungen).
    //
    // Mollie-Optional (Kombi-Modus): Erlaubt der Kurs Online-Zahlung ADDITIV zu den
    // eigenen Methoden (`allowMolliePayment===true && mollieOptional===true`), gibt es
    // KEINEN dedizierten Mollie-Block — stattdessen rendert die kombinierte flache
    // PaymentMethodSection (eigene Methoden + Mollie-Zeilen in EINER Tabelle, inkl.
    // Overlap-Filter). Nur der reine Pflicht-Modus (`mollieOptional !== true`) zeigt
    // den exklusiven MollieMethodsBlock. `!== true` behandelt `null`/`undefined` als
    // Pflicht (rueckwaertskompatibel mit Kursen ohne `mollieOptional`-Feld).
    const isMollieMandatoryMode = course.allowMolliePayment === true && course.mollieOptional !== true && mollieMethods.length > 0
    const isMollieCombinedMode = course.allowMolliePayment === true && course.mollieOptional === true && mollieMethods.length > 0

    // Gleiche Regel wie das Submit-Gate in BookingSummary: maxWaitlist null = unbegrenzt.
    const prerequisiteWaitlistFull = prerequisiteWaitlistOnly && course.maxWaitlist != null && course.waitListCount >= course.maxWaitlist

    return (
        <>
            {prerequisiteWaitlistFull && (
                <Alert
                    type='error'
                    showIcon
                    data-testid='prerequisite-waitlist-full'
                    style={{ marginBottom: sectionGap }}
                    message={t("bookingStepSummary.prerequisiteWaitlist.fullTitle", "Die Warteliste dieses Kurses ist voll")}
                    description={t(
                        "bookingStepSummary.prerequisiteWaitlist.fullDescription",
                        "Dem Teilnehmer fehlt der Voraussetzungskurs, deshalb wäre nur ein Eintrag auf die Warteliste möglich. Die Warteliste ist aber bereits voll."
                    )}
                />
            )}
            {prerequisiteWaitlistOnly && !prerequisiteWaitlistFull && (
                <Alert
                    type='warning'
                    showIcon
                    data-testid='prerequisite-waitlist-hint'
                    style={{ marginBottom: sectionGap }}
                    message={t("bookingStepSummary.prerequisiteWaitlist.title", "Voraussetzung fehlt, Eintrag nur auf die Warteliste möglich")}
                    description={t(
                        "bookingStepSummary.prerequisiteWaitlist.description",
                        "Dem Teilnehmer fehlt der Voraussetzungskurs. Sie buchen deshalb keinen Platz, sondern tragen ihn auf die Warteliste ein. Ein Platz wird erst später zugeteilt, falls das möglich ist."
                    )}
                />
            )}
            <Collapse
                ghost
                expandIconPosition='end'
                defaultActiveKey={media === "xs" ? [] : ["courseDetails"]}
                className={css.collapse}
                expandIcon={(panelProps) => {
                    return (
                        <div className={css.collapseIcon}>
                            {panelProps.isActive ? (
                                <DownCircleTwoTone className={css.collapseIcon} /* style={{ marginTop: 20 }} */ />
                            ) : (
                                <RightCircleFilled style={{ /* marginTop: 20, */ color: blue[5] }} className={css.collapseIcon} />
                            )}
                        </div>
                    )
                }}
            >
                <SummarySection
                    style={{ paddingTop: sectionGap }}
                    title={"Kursinformationen"}
                    content={
                        <h1
                            style={{
                                fontWeight: 700,
                                color: "rgba(0,0,0,.65)",
                                margin: 0,
                            }}
                        >
                            {course.courseType.name}
                        </h1>
                    }
                    extra={course.prefixedCourseNumber}
                />
                <SummarySection
                    style={{ paddingTop: sectionGap }}
                    title={"Kurseinheiten"}
                    content={
                        <>
                            <CourseLessonTable courseId={course.id} totalPrice={course.coursePrice[0].grossPrice} paymentInterval={course.paymentInterval ?? PaymentInterval.PerLesson} />
                        </>
                    }
                />
                {/* EINE Sektion "Bezahlmethode" — konstant gerendert, egal ob durch Guthaben
                    voll gedeckt. Wichtig: KEINE zusätzliche Geschwister-Sektion je nach
                    fullyCoveredByCredit einblenden — das würde die Geschwister-Reihenfolge im
                    Collapse verschieben und (mangels key-Props) die nachfolgende
                    BookingPricePreview remounten, wodurch deren "Guthaben anrechnen"-Toggle
                    zurückspringt. Der "Keine Zahlung erforderlich"-Hinweis lebt daher INNERHALB
                    dieser Sektion. Der Picker bleibt sichtbar, damit die gewählte Methode auch
                    bei 0-€-Buchung in die Buchung/Rechnung fliesst (sonst paymentMethodId=null). */}
                {booker.attendees && booker.attendees.length > 0 && (
                    <SummarySection
                        style={{ paddingTop: sectionGap }}
                        title={"Bezahlmethode"}
                        content={
                            <>
                                {fullyCoveredByCredit && (
                                    <Alert
                                        type='success'
                                        showIcon
                                        data-testid='credit-fully-covered-payment-info'
                                        style={{ marginBottom: margin.s }}
                                        message={
                                            (bookingData?.creditedBalance ?? 0) > 0
                                                ? t("creditRedemption.noPaymentRequired", "Keine Zahlung erforderlich - der Betrag ist vollständig durch das Guthaben gedeckt.")
                                                : t("discountCode.noPaymentRequired", "Keine Zahlung erforderlich - der Rabattcode deckt den vollen Betrag.")
                                        }
                                    />
                                )}
                                {isMollieMandatoryMode && mode === "PlaceReservation" ? (
                                    // Wartelisten-Fall (Kurs voll): Die Methodenwahl entfaellt — der
                                    // Server lehnt einen Zahlungsstart fuer Wartelistenplaetze ab
                                    // (Zahlung erst beim Nachruecken). Hinweis INNERHALB der Sektion
                                    // rendern (keine Geschwister-Sektion — siehe Remount-Kommentar oben).
                                    <Alert
                                        type='info'
                                        showIcon
                                        data-testid='waitlist-checkout-hint'
                                        message={
                                            prerequisiteWaitlistOnly
                                                ? t(
                                                      "mollieCheckout.waitlist.prerequisiteCheckoutHint",
                                                      "Eine Zahlung ist erst nötig, wenn ein Platz zugeteilt wird; Sie erhalten dann eine E-Mail mit Zahlungslink.",
                                                  )
                                                : t(
                                                      "mollieCheckout.waitlist.checkoutHint",
                                                      "Dieser Kurs ist aktuell ausgebucht – du wirst auf die Warteliste gesetzt. Eine Zahlung ist erst nötig, wenn dein Platz zugeteilt wird; du erhältst dann eine E-Mail mit Zahlungslink.",
                                                  )
                                        }
                                    />
                                ) : isMollieMandatoryMode ? (
                                    <div className={mollieCss.mollieBg} data-testid="mollie-mode-wrapper">
                                        <div className={mollieCss.mollieHeader}>
                                            <span className={mollieCss.mollieLogoBadge}>
                                                Mollie
                                            </span>
                                            <span className={mollieCss.secureNote}>
                                                Sichere Zahlung – die Auswahl wird direkt an Mollie übergeben.
                                            </span>
                                        </div>
                                        <MollieMethodsBlock
                                            courseId={course.id}
                                            selectedMethod={selectedMollieMethod}
                                            onMethodSelect={onMollieMethodSelect}
                                        />
                                    </div>
                                ) : (
                                    <>
                                        {isMollieCombinedMode && (
                                            <Alert
                                                type='info'
                                                showIcon
                                                data-testid='mollie-combined-hint'
                                                style={{ marginBottom: margin.s }}
                                                message={t(
                                                    "mollieCheckout.combined.exclusiveHint",
                                                    "Bitte wähle genau eine Zahlungsart – entweder eine Online-Zahlung oder eine der eigenen Methoden.",
                                                )}
                                            />
                                        )}
                                        {paymentMethodIncomplete && (
                                            <Alert
                                                type='warning'
                                                showIcon
                                                data-testid='payment-method-incomplete-notice'
                                                style={{ marginBottom: margin.s }}
                                                message={t(
                                                    "bookingStepSummary.paymentMethodIncomplete",
                                                    "Deine Lastschrift-Daten sind unvollständig. Bitte trage Kontoinhaber und IBAN ein oder wähle eine andere Bezahlmethode.",
                                                )}
                                            />
                                        )}
                                        {paymentMethodSection()}
                                    </>
                                )}
                            </>
                        }
                    />
                )}
                {course &&
                    bookingData &&
                    appSettings &&
                    (courseHasTwoAllowedPaymentOptions ? (
                        <SummarySection
                            style={{ paddingTop: sectionGap }}
                            title='Preisübersicht'
                            content={
                                <BookingPricePreview
                                    course={course}
                                    updateBookingData={updateBookingData}
                                    bookingData={bookingData}
                                    bookerCredit={booker.credit}
                                    creditRedemptionEnabled={appSettings.creditRedemptionEnabled === true}
                                    discountCodesEnabled={appSettings.discountCodesEnabled === true}
                                    showTotalPrice={appSettings.showTotalPriceInCheckout !== false}
                                    onOpenAmountChange={onOpenAmountChange}
                                    onQuoteChange={onQuoteChange}
                                    refreshNonce={refreshNonce}
                                    onCourseOutdated={onCourseOutdated}
                                    waitlistOnly={prerequisiteWaitlistOnly}
                                />
                            }
                        />
                    ) : (
                        <BookingPricePreview
                            course={course}
                            updateBookingData={updateBookingData}
                            bookingData={bookingData}
                            bookerCredit={booker.credit}
                            creditRedemptionEnabled={appSettings.creditRedemptionEnabled === true}
                            discountCodesEnabled={appSettings.discountCodesEnabled === true}
                            showTotalPrice={appSettings.showTotalPriceInCheckout !== false}
                            onOpenAmountChange={onOpenAmountChange}
                            onQuoteChange={onQuoteChange}
                            refreshNonce={refreshNonce}
                            onCourseOutdated={onCourseOutdated}
                            waitlistOnly={prerequisiteWaitlistOnly}
                        />
                    ))}
                {booker && (
                    <>
                        <SummarySection style={{ paddingTop: sectionGap }} title='Notizen' content={notesSection()} />
                        <ParticipationPrerequisites
                            courseTypeId={course.courseType.id}
                            setCheckedOptions={setCheckedOptions}
                            checkedOptions={checkedOptions}
                            setRequermentGroupsMet={setRequermentGroupsMet}
                            requermentGroupsMet={requermentGroupsMet}
                            sectionGap={sectionGap}
                        />
                        <SummarySection
                            style={{ paddingTop: sectionGap }}
                            content={
                                <OptionalInputFields
                                    booker={booker}
                                    bookingData={bookingData}
                                    updateBookingData={updateBookingData}
                                    appSettings={appSettings}
                                    showAlert={showAlert}
                                    inputFieldValues={inputFieldValues}
                                    setInputFieldValues={setInputFieldValues}
                                />
                            }
                            title='Weitere Angaben'
                        />
                        {appSettings?.billingAddressInCheckoutEnabled === true && (
                            <SummarySection
                                style={{ paddingTop: sectionGap }}
                                content={
                                    <BillingAddressInput bookingData={bookingData} updateBookingData={updateBookingData} showAlert={showAlert} />
                                }
                                title={t("bookingStepSummary.billingAddress.title", "Rechnungsadresse")}
                            />
                        )}
                    </>
                )}
            </Collapse>
        </>
    )
}

export default memo(SummaryRight)
